import api from './api';

/**
 * Create or update a draft envelope
 * API: POST /api/envelope-create (multipart/form-data)
 * All parameters are optional - allows step-by-step envelope creation
 * Envelopes automatically create template entries
 * 
 * @param {Object} data - Envelope data (all optional)
 * @param {number} data.envelope_id - Optional: Update existing envelope by ID
 * @param {string} data.name - Template name (default: "Untitled Envelope")
 * @param {File} data.document_file - Single PDF document (Max: 10MB)
 * @param {Array<File>} data.document_files - Multiple PDF documents (Max: 10MB each)
 * @param {Array<number>} data.document_ids - Array of existing document IDs to attach
 * @param {Array<number>} data.document_page_counts - Page counts aligned with document_files indices
 * @param {number} data.document_page_count - Page count for single document_file upload
 * @param {Array<number>} data.document_orders - Display order aligned with document_files indices
 * @param {number} data.document_order - Display order for single document_file upload
 * @param {Object<string, number>} data.document_page_counts_by_document_id - Page counts for attached document_ids
 * @param {string} data.document_title - Document title for new documents
 * @param {Array<string>} data.document_titles - Array of titles for multiple documents
 * @param {Array<string>} [data.document_client_ids] - Client UUIDs aligned with document_files (optional; backend may echo for correlation)
 * @param {string} [data.document_client_id] - Client UUID for single document_file upload
 * @param {string} data.subject - Email subject line
 * @param {string} data.message - Custom message body
 * @param {string} data.reminder_frequency - e.g., 'daily', 'weekly', 'every_3_days', 'never'
 * @param {string} data.workflow_type - 'parallel' or 'sequential' (default: 'parallel')
 * @param {number} data.created_by_user_id - User ID for audit trail (optional)
 * @param {Array} data.recipients - Array of recipient objects {email, name, type, signature_order}
 * @param {Array} data.fields - Array of field objects {recipient_email, field_type, x_position, y_position, width, height, is_required, page_number}
 * @returns {Promise} Response with envelope_id, document_ids, status, etc.
 */
export const createDraftEnvelope = async (data, options = {}) => {
  try {
    // const user = getCurrentUser();
    const formData = new FormData();
    
    console.log('📮 Creating/updating draft envelope:', {
      envelope_id: data.envelope_id,
      hasDocumentFile: !!data.document_file,
      hasDocumentFiles: !!data.document_files,
      documentFilesCount: data.document_files?.length || 0,
      hasDocumentIds: !!data.document_ids,
      documentIdsCount: data.document_ids?.length || 0,
      recipientsCount: data.recipients?.length || 0,
      fieldsCount: data.fields?.length || 0
    });
    
    // Add envelope_id if provided (for updating existing envelope)
    if (data.envelope_id) {
      formData.append('envelope_id', data.envelope_id);
      console.log('✅ Added envelope_id:', data.envelope_id);
    }
    
    // Add name for template entry
    if (data.name) {
      formData.append('name', data.name);
      console.log('✅ Added name:', data.name);
    }
    
    // Add document files (multiple documents)
    if (data.document_files && Array.isArray(data.document_files)) {
      console.log('📄 Adding multiple document files:', data.document_files.length);
      data.document_files.forEach((file, index) => {
        console.log(`  - File ${index}: ${file.name} (${file.size} bytes)`);
        formData.append(`document_files[${index}]`, file);
      });
    } else if (data.document_file) {
      // Single document
      console.log('📄 Adding single document file:', {
        name: data.document_file.name,
        size: data.document_file.size,
        type: data.document_file.type
      });
      formData.append('document_file', data.document_file);
      console.log('✅ document_file appended to FormData');
    } else {
      console.warn('⚠️ No document_file or document_files provided');
    }
    
    // Add document IDs (for attaching existing documents)
    if (data.document_ids && Array.isArray(data.document_ids)) {
      data.document_ids.forEach((docId, index) => {
        formData.append(`document_ids[${index}]`, docId);
      });
    }
    
    // Add document titles
    if (data.document_titles && Array.isArray(data.document_titles)) {
      console.log('📝 Adding document titles:', data.document_titles);
      data.document_titles.forEach((title, index) => {
        formData.append(`document_titles[${index}]`, title);
      });
    } else if (data.document_title) {
      console.log('📝 Adding document title:', data.document_title);
      formData.append('document_title', data.document_title);
    }

    const hasMultiDocumentUpload = Array.isArray(data.document_files) && data.document_files.length > 0;
    const hasSingleDocumentUpload = Boolean(data.document_file);
    const hasDocumentIds = Array.isArray(data.document_ids) && data.document_ids.length > 0;

    if (hasMultiDocumentUpload && Array.isArray(data.document_page_counts)) {
      data.document_page_counts.forEach((count, index) => {
        formData.append(`document_page_counts[${index}]`, count);
      });
    } else if (hasSingleDocumentUpload && data.document_page_count != null && data.document_page_count !== '') {
      formData.append('document_page_count', data.document_page_count);
    }

    if (hasMultiDocumentUpload && Array.isArray(data.document_client_ids) && data.document_client_ids.length > 0) {
      data.document_client_ids.forEach((clientId, index) => {
        if (clientId != null && String(clientId).trim() !== '') {
          formData.append(`document_client_ids[${index}]`, String(clientId).trim());
        }
      });
    } else if (hasSingleDocumentUpload && data.document_client_id != null && String(data.document_client_id).trim() !== '') {
      formData.append('document_client_id', String(data.document_client_id).trim());
    }

    if (hasMultiDocumentUpload && Array.isArray(data.document_orders)) {
      data.document_orders.forEach((order, index) => {
        formData.append(`document_orders[${index}]`, order);
      });
    } else if (hasSingleDocumentUpload && data.document_order != null && data.document_order !== '') {
      formData.append('document_order', data.document_order);
    }

    if (hasDocumentIds && data.document_page_counts_by_document_id != null) {
      const countsById = data.document_page_counts_by_document_id;
      if (typeof countsById === 'string') {
        formData.append('document_page_counts_by_document_id', countsById);
      } else if (typeof countsById === 'object' && !Array.isArray(countsById)) {
        formData.append('document_page_counts_by_document_id', JSON.stringify(countsById));
      }
    }

    // Log FormData contents for debugging
    console.log('📦 FormData contents:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }
    
    // Add metadata
    if (data.subject !== undefined) formData.append('subject', data.subject);
    if (data.message !== undefined) formData.append('message', data.message);
    if (data.reminder_frequency !== undefined) formData.append('reminder_frequency', data.reminder_frequency);
    if (data.workflow_type) formData.append('workflow_type', data.workflow_type);
    if (data.created_by_user_id) formData.append('created_by_user_id', data.created_by_user_id);
    
    // Add recipients (API format: {id (optional), email, name, type, signature_order, signee_color})
    // id is optional - if provided, updates existing recipient; if omitted, creates new
    if (data.recipients && Array.isArray(data.recipients)) {
      data.recipients.forEach((recipient, index) => {
        // Include id if provided (for updates)
        if (recipient.id !== undefined && recipient.id !== null) {
          formData.append(`recipients[${index}][id]`, recipient.id);
        }
        if (recipient.email) formData.append(`recipients[${index}][email]`, recipient.email);
        if (recipient.name) formData.append(`recipients[${index}][name]`, recipient.name);
        if (recipient.type) formData.append(`recipients[${index}][type]`, recipient.type);
        if (recipient.signature_order !== undefined && recipient.signature_order !== null) {
          formData.append(`recipients[${index}][signature_order]`, recipient.signature_order);
        }
        if (recipient.signee_color) {
          formData.append(`recipients[${index}][signee_color]`, recipient.signee_color);
        }
        if (recipient.signee_placeholder) {
          formData.append(`recipients[${index}][signee_placeholder]`, recipient.signee_placeholder);
        }
      });
    }
    
    // Add fields (API format: {id or field_id (optional), document_id, recipient_id, field_type, page_number, x_position, y_position, width, height, is_required, read_only})
    // id or field_id is optional - if provided, updates existing field; if omitted, creates new (but may be skipped to avoid duplicates)
    if (data.fields && Array.isArray(data.fields)) {
      data.fields.forEach((field, index) => {
        // Include id or field_id if provided (for updates)
        if (field.id !== undefined && field.id !== null) {
          formData.append(`fields[${index}][id]`, field.id);
        } else if (field.field_id !== undefined && field.field_id !== null) {
          formData.append(`fields[${index}][field_id]`, field.field_id);
        }
        if (field.document_id !== undefined && field.document_id !== null) {
          formData.append(`fields[${index}][document_id]`, field.document_id);
        }
        if (field.recipient_id !== undefined && field.recipient_id !== null) {
          formData.append(`fields[${index}][recipient_id]`, field.recipient_id);
        }
        if (field.field_type) formData.append(`fields[${index}][field_type]`, field.field_type);
        if (field.field_name) formData.append(`fields[${index}][field_name]`, field.field_name);
        if (field.page_number !== undefined) formData.append(`fields[${index}][page_number]`, field.page_number);
        if (field.x_position !== undefined) formData.append(`fields[${index}][x_position]`, field.x_position);
        if (field.y_position !== undefined) formData.append(`fields[${index}][y_position]`, field.y_position);
        if (field.width !== undefined) formData.append(`fields[${index}][width]`, field.width);
        if (field.height !== undefined) formData.append(`fields[${index}][height]`, field.height);
        if (field.is_required !== undefined) formData.append(`fields[${index}][is_required]`, field.is_required ? '1' : '0');
        if (field.read_only !== undefined) formData.append(`fields[${index}][read_only]`, field.read_only ? '1' : '0');
        if (field.field_value !== undefined && field.field_value !== null) {
          formData.append(`fields[${index}][field_value]`, field.field_value);
        }
        if (field.field_options !== undefined && field.field_options !== null) {
          formData.append(`fields[${index}][field_options]`, field.field_options);
        }
      });
    }
    
    const response = await api.post('/envelope-create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
    });
    
    console.log('✅ Envelope created/updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating/updating draft envelope:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Update draft envelope (update document, recipients, or fields separately)
 * Note: Based on API, we'll update document, recipients, and fields separately
 */
export const updateDraftDocument = async (documentId, file, title) => {
  try {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (title) formData.append('title', title);
    
    const response = await api.put(`/document-update/${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

/**
 * Get draft envelope by ID (alias for getEnvelope)
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const getDraftEnvelope = async (envelopeId) => {
  return getEnvelope(envelopeId);
};

/**
 * Get list of envelopes
 * API: GET /api/envelope-list
 * @param {string} status - Optional: Filter by status ('draft', 'sent', 'completed')
 * @returns {Promise} Response object with { success, data, filters, meta }
 */
export const getEnvelopes = async (status = null) => {
  try {
    const params = {};
    if (status) {
      params.status = status;
    }
    console.log('📋 Fetching envelopes:', params);
    const response = await api.get('/envelope-list', { params });
    console.log('✅ Envelopes fetched successfully:', response.data);
    // API returns { success: true, data: [...], filters: {...}, meta: {...} }
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching envelopes:', error);
    throw error;
  }
};

/**
 * Get user's draft envelopes
 * API: GET /api/envelope-list?status=draft
 * @returns {Promise} Array of draft envelopes (extracted from response.data)
 */
export const getDraftEnvelopes = async () => {
  try {
    const response = await getEnvelopes('draft');
    // Extract the data array from the API response
    // API returns: { success: true, data: [...], filters: {...}, meta: {...} }
    // Return the data array for backward compatibility
    return response.data || [];
  } catch (error) {
    console.error('Error fetching draft envelopes:', error);
    throw error;
  }
};

/**
 * Send envelope (finalize draft)
 * API: POST /api/envelope-send
 * @param {number} envelopeId - Envelope ID (must be in draft status)
 * @returns {Promise} Response with envelope data and status
 */
export const sendEnvelope = async (envelopeId) => {
  try {
    console.log('📧 Sending envelope:', envelopeId);
    const payload = {
      envelope_id: envelopeId
    };
    
    console.log('📤 Sending envelope request:', payload);
    const response = await api.post('/envelope-send', payload);
    console.log('✅ Envelope sent successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error sending envelope:', error);
    if (error.response?.data) {
      console.error('❌ Error response:', error.response.data);
    }
    throw error;
  }
};

/**
 * Pre-send sync: reconcile envelope metadata (documents without files, recipients, fields)
 * before final send. Backend may return missing documents to upload next.
 * API: POST /api/envelope/{envelope_id}/pre-send-sync
 *
 * @param {number|string} envelopeId - Envelope ID
 * @param {Object} payload - Full sync body (subject, message, documents[], recipients[], fields[], …)
 * @returns {Promise} API response (shape may include missing_documents, success, etc.)
 */
export const preSendEnvelopeSync = async (envelopeId, payload) => {
  const id = envelopeId != null && envelopeId !== '' ? String(envelopeId).trim() : '';
  if (!id) {
    throw new Error('Envelope id is required for pre-send sync.');
  }
  try {
    console.log('🔄 Pre-send envelope sync:', { envelope_id: id });
    const response = await api.post(`/envelope/${id}/pre-send-sync`, payload);
    console.log('✅ Pre-send sync response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error in pre-send envelope sync:', error);
    if (error.response?.data) {
      console.error('❌ Pre-send sync error body:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get envelope by ID
 * API: GET /api/envelope/{envelope_id}
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const getEnvelope = async (envelopeId) => {
  try {
    const response = await api.get(`/envelope/${envelopeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching envelope:', error);
    throw error;
  }
};

/**
 * Revoke envelope
 * API: POST /api/envelope-revoke/{envelope_id}
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const revokeEnvelope = async (envelopeId) => {
  try {
    console.log('🚫 Revoking envelope:', envelopeId);
    const response = await api.post(`/envelope-revoke/${envelopeId}`);
    console.log('✅ Envelope revoked successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error revoking envelope:', error);
    throw error;
  }
};

/**
 * Copy an envelope into a new editable draft
 * API: POST /api/envelope-copy/{id}
 * @param {number|string} envelopeId - Source envelope ID
 * @returns {Promise}
 */
export const copyEnvelope = async (envelopeId) => {
  try {
    console.log('📋 Copying envelope:', envelopeId);
    const response = await api.post(`/envelope-copy/${envelopeId}`);
    console.log('✅ Envelope copied successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error copying envelope:', error);
    throw error;
  }
};

/**
 * Process next sequential recipients
 * API: POST /api/envelope-process-next/{envelope_id}
 * Used internally when a signee completes signing in sequential workflow
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const processNextSequentialRecipients = async (envelopeId) => {
  try {
    console.log('⏭️ Processing next sequential recipients for envelope:', envelopeId);
    const response = await api.post(`/envelope-process-next/${envelopeId}`);
    console.log('✅ Next recipients processed:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error processing next recipients:', error);
    throw error;
  }
};

/**
 * Resend signature request email to a specific envelope recipient
 * API: POST /api/envelope-recipient-resend/{recipient_id}
 * @param {number|string} recipientId - Envelope recipient ID
 * @returns {Promise}
 */
export const resendEnvelopeRecipient = async (recipientId) => {
  try {
    console.log('📨 Resending envelope recipient email:', recipientId);
    const response = await api.post(`/envelope-recipient-resend/${recipientId}`);
    console.log('✅ Recipient resend triggered:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error resending recipient email:', error);
    throw error;
  }
};

/**
 * Send completed envelope assets to a specific recipient
 * API: POST /api/envelope-recipient-send-completed/{recipient_id}
 * Body: { send_type: "documents" | "certificate" }
 * @param {number|string} recipientId - Envelope recipient ID
 * @param {"documents"|"certificate"} sendType - Asset type to email
 * @returns {Promise}
 */
export const sendCompletedEnvelopeToRecipient = async (recipientId, sendType) => {
  try {
    const normalizedSendType = String(sendType || '').trim().toLowerCase();
    if (!['documents', 'certificate'].includes(normalizedSendType)) {
      throw new Error('Invalid send_type. Expected "documents" or "certificate".');
    }

    console.log('📩 Sending completed envelope assets to recipient:', {
      recipientId,
      send_type: normalizedSendType,
    });
    const response = await api.post(`/envelope-recipient-send-completed/${recipientId}`, {
      send_type: normalizedSendType,
    });
    console.log('✅ Completed envelope email triggered:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error sending completed envelope assets:', error);
    throw error;
  }
};

/**
 * Generate completion certificate
 * API: POST /api/envelope-generate-certificate/{envelope_id}
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const generateCompletionCertificate = async (envelopeId) => {
  try {
    console.log('📜 Generating completion certificate for envelope:', envelopeId);
    const response = await api.post(`/envelope-generate-certificate/${envelopeId}`);
    console.log('✅ Completion certificate generated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error generating completion certificate:', error);
    throw error;
  }
};

/**
 * View/download completion certificate PDF
 * API: GET /api/envelope/{envelope_id}/certificate
 * Returns inline PDF stream
 * @param {number|string} envelopeId - Envelope ID
 * @returns {Promise<ArrayBuffer>}
 */
export const getEnvelopeCertificatePdf = async (envelopeId) => {
  try {
    const response = await api.get(`/envelope/${envelopeId}/certificate`, {
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching completion certificate PDF:', error);
    throw error;
  }
};

/**
 * View/download signed document PDF for completed envelope
 * API: GET /api/envelope/{id}/signed-document/{envelopeDocumentId}
 * Returns inline PDF stream
 * @param {number|string} envelopeId - Envelope ID
 * @param {number|string} envelopeDocumentId - Envelope document ID
 * @returns {Promise<ArrayBuffer>}
 */
export const getEnvelopeSignedDocumentPdf = async (envelopeId, envelopeDocumentId) => {
  try {
    const response = await api.get(`/envelope/${envelopeId}/signed-document/${envelopeDocumentId}`, {
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching signed document PDF:', error);
    throw error;
  }
};

/**
 * Get document envelope summary
 * API: GET /api/document-envelope-summary/{document_id}
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const getDocumentEnvelopeSummary = async (documentId) => {
  try {
    const response = await api.get(`/document-envelope-summary/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching document envelope summary:', error);
    throw error;
  }
};

/**
 * Generate temporary signed URLs for multiple documents.
 * API: POST /api/document-files-batch
 * @param {Array<number|string>} documentIds - Document IDs (up to backend max)
 * @param {number} ttlMinutes - Signed URL validity (1..120, backend validated)
 * @returns {Promise<{items: Array, meta: Object}>}
 */
export const getDocumentFileBatchUrls = async (documentIds, ttlMinutes = 15) => {
  try {
    const normalizedIds = (Array.isArray(documentIds) ? documentIds : [])
      .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
      .filter((id) => Number.isFinite(id));

    if (normalizedIds.length === 0) {
      return {
        items: [],
        meta: {
          requested_count: 0,
          available_count: 0,
          missing_document_ids: [],
          ttl_minutes: ttlMinutes,
        },
      };
    }

    const response = await api.post(
      '/document-files-batch',
      {
        document_ids: normalizedIds,
        ttl_minutes: ttlMinutes,
      },
      { skipRequestDedup: true }
    );

    return {
      items: Array.isArray(response?.data?.data) ? response.data.data : [],
      meta: response?.data?.meta || {},
    };
  } catch (error) {
    console.error('Error fetching batched document file URLs:', error);
    throw error;
  }
};

/**
 * Generate temporary signed ZIP URLs for document batches (server chunks by 10 docs).
 * API: POST /api/document-files-batch-zip
 * @param {Array<number|string>} documentIds - Document IDs
 * @param {number} ttlMinutes - Signed URL validity (1..120, backend validated)
 * @returns {Promise<{items: Array, meta: Object}>}
 */
export const getDocumentFileBatchZipUrls = async (documentIds, ttlMinutes = 30) => {
  try {
    const normalizedIds = (Array.isArray(documentIds) ? documentIds : [])
      .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
      .filter((id) => Number.isFinite(id));

    if (normalizedIds.length === 0) {
      return {
        items: [],
        meta: {
          requested_count: 0,
          available_count: 0,
          missing_document_ids: [],
          ttl_minutes: ttlMinutes,
        },
      };
    }

    const response = await api.post(
      '/document-files-batch-zip',
      {
        document_ids: normalizedIds,
        ttl_minutes: ttlMinutes,
      },
      { skipRequestDedup: true }
    );

    const payload = response?.data || {};
    const payloadData = payload?.data;
    const items =
      (Array.isArray(payloadData) && payloadData) ||
      (Array.isArray(payloadData?.items) && payloadData.items) ||
      (Array.isArray(payloadData?.batches) && payloadData.batches) ||
      (Array.isArray(payloadData?.zip_batches) && payloadData.zip_batches) ||
      (Array.isArray(payloadData?.archives) && payloadData.archives) ||
      (Array.isArray(payload?.items) && payload.items) ||
      (Array.isArray(payload?.batches) && payload.batches) ||
      (Array.isArray(payload?.zip_batches) && payload.zip_batches) ||
      (Array.isArray(payload?.archives) && payload.archives) ||
      (payloadData && typeof payloadData === 'object' && (payloadData.zip_url || payloadData.archive || payloadData.url) ? [payloadData] : null) ||
      (payload && typeof payload === 'object' && (payload.zip_url || payload.archive || payload.url) ? [payload] : null) ||
      [];

    const meta = payload?.meta || payloadData?.meta || {};

    return {
      items,
      meta,
    };
  } catch (error) {
    console.error('Error fetching document batch ZIP URLs:', error);
    throw error;
  }
};

/**
 * Create document
 * Backend spec: accepts file, title, description, page_count, order (multipart/form-data)
 * @param {File} file - PDF file
 * @param {string} title - Document title
 * @param {string} description - Optional description
 * @param {Object} options - Additional options
 * @param {number} [options.page_count] - Required page count for API validation
 * @param {number} [options.order] - Document order within envelope/template
 * @param {string} [options.client_document_id] - Client-generated id echoed by API for correlation
 * @param {Function} [options.onUploadProgress] - Axios upload progress callback
 * @returns {Promise}
 */
export const createDocument = async (file, title, description = '', options = {}) => {
  try {
    console.log('📄 Creating document:', {
      fileName: file?.name,
      title,
      description,
      page_count: options.page_count,
      order: options.order,
    });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    if (description) formData.append('description', description);
    if (options.page_count != null && options.page_count !== '') {
      formData.append('page_count', options.page_count);
    }
    if (options.order != null && options.order !== '') {
      formData.append('order', options.order);
    }
    if (options.client_document_id != null && String(options.client_document_id).trim() !== '') {
      formData.append('client_document_id', String(options.client_document_id).trim());
    }

    // Log what we're sending
    console.log('📤 Sending to /document-create with FormData fields:', {
      hasFile: !!file,
      title,
      hasDescription: !!description,
      page_count: options.page_count,
      order: options.order,
    });

    const response = await api.post('/document-create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
    });
    console.log('✅ Document created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating document:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
      console.error('❌ Full error response:', JSON.stringify(error.response.data, null, 2));
    }
    throw error;
  }
};

/**
 * Delete a document
 * API: DELETE /api/document-delete/{document_id}
 * @param {number} documentId - The document ID to delete
 * @returns {Promise}
 */
export const deleteDocument = async (documentId) => {
  try {
    console.log('🗑️ Deleting document:', documentId);
    const response = await api.delete(`/document-delete/${documentId}`);
    console.log('✅ Document deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting document:', error);
    throw error;
  }
};

/**
 * Create a single recipient
 * @param {number} documentId - Document ID
 * @param {Object} recipientData - Recipient data
 * @param {string} recipientData.recipient_type - 'signee' | 'review_recipient' | 'copy_recipient'
 * @param {string} recipientData.signee_placeholder - Placeholder like 'signee_1'
 * @param {string} recipientData.email - Recipient email
 * @param {string} recipientData.name - Recipient name
 * @param {string} recipientData.signee_color - Color for signee (optional)
 * @param {number} recipientData.signature_order - Order for sequential workflow (optional)
 * @param {string} recipientData.workflow_type - 'parallel' or 'sequential' (optional)
 * @returns {Promise}
 */
export const createRecipient = async (documentId, recipientData) => {
  try {
    console.log('👤 Creating recipient:', { documentId, recipientData });
    const payload = {
      document_id: documentId,
      recipient_type: recipientData.recipient_type,
      signee_placeholder: recipientData.signee_placeholder,
      email: recipientData.email,
      name: recipientData.name,
      signee_color: recipientData.signee_color,
      signature_order: recipientData.signature_order,
      workflow_type: recipientData.workflow_type
    };
    
    const response = await api.post('/document-recipient-create', payload);
    console.log('✅ Recipient created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating recipient:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Bulk create recipients
 * @param {number} documentId - Document ID
 * @param {Array} recipients - Array of recipient objects
 * @returns {Promise}
 */
export const bulkCreateRecipients = async (documentId, recipients) => {
  try {
    console.log('👥 Bulk creating recipients:', { documentId, count: recipients.length });
    const payload = {
      document_id: documentId,
      recipients: recipients.map(r => ({
        recipient_type: r.recipient_type,
        signee_placeholder: r.signee_placeholder,
        email: r.email,
        name: r.name,
        signee_color: r.signee_color,
        signature_order: r.signature_order,
        workflow_type: r.workflow_type
      }))
    };
    
    console.log('📤 Sending bulk create request:', payload);
    const response = await api.post('/document-recipient-bulk-create', payload);
    console.log('✅ Recipients created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating recipients:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get recipients for a document
 * @param {number} documentId - Document ID
 * @param {Object} params - Query parameters (recipient_type, status)
 * @returns {Promise}
 */
export const getDocumentRecipients = async (documentId, params = {}) => {
  try {
    const response = await api.get(`/document-recipients/${documentId}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching document recipients:', error);
    throw error;
  }
};

/**
 * Get a single recipient by ID
 * @param {number} recipientId - Recipient ID
 * @returns {Promise}
 */
export const getRecipient = async (recipientId) => {
  try {
    const response = await api.get(`/document-recipient/${recipientId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recipient:', error);
    throw error;
  }
};

/**
 * Update a recipient
 * @param {number} recipientId - Recipient ID
 * @param {Object} recipientData - Updated recipient data
 * @returns {Promise}
 */
export const updateRecipient = async (recipientId, recipientData) => {
  try {
    console.log('🔄 Updating recipient:', { recipientId, recipientData });
    const response = await api.put(`/document-recipient-update/${recipientId}`, recipientData);
    console.log('✅ Recipient updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating recipient:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Delete a recipient
 * @param {number} recipientId - Recipient ID
 * @returns {Promise}
 */
export const deleteRecipient = async (recipientId) => {
  try {
    console.log('🗑️ Deleting recipient:', recipientId);
    const response = await api.delete(`/document-recipient-delete/${recipientId}`);
    console.log('✅ Recipient deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting recipient:', error);
    throw error;
  }
};

/**
 * Get recipients by type
 * @param {number} documentId - Document ID
 * @param {string} recipientType - 'signee' | 'review_recipient' | 'copy_recipient'
 * @returns {Promise}
 */
export const getRecipientsByType = async (documentId, recipientType) => {
  try {
    const response = await api.get(`/document-recipients-by-type/${documentId}/${recipientType}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recipients by type:', error);
    throw error;
  }
};

/**
 * Assign recipient to fields
 * @param {number} recipientId - Recipient ID
 * @param {Array} fieldIds - Array of field IDs
 * @returns {Promise}
 */
export const assignRecipientToFields = async (recipientId, fieldIds) => {
  try {
    console.log('🔗 Assigning recipient to fields:', { recipientId, fieldIds });
    const response = await api.post(`/document-recipient-assign-fields/${recipientId}`, {
      field_ids: fieldIds
    });
    console.log('✅ Recipient assigned to fields successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error assigning recipient to fields:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get recipients summary
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const getRecipientsSummary = async (documentId) => {
  try {
    const response = await api.get(`/document-recipients-summary/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recipients summary:', error);
    throw error;
  }
};

/**
 * Create a single field
 * API: POST /api/document-field-create
 * Recommended for ongoing field management
 * @param {Object} fieldData - Field data
 * @param {number} fieldData.document_id - Document ID
 * @param {number} fieldData.recipient_id - Recipient ID
 * @param {string} fieldData.field_name - Field name
 * @param {string} fieldData.field_type - Field type (signature, text, date, checkbox, initials, cnic)
 * @param {number} fieldData.page_number - Page number
 * @param {number} fieldData.x_position - X position
 * @param {number} fieldData.y_position - Y position
 * @param {number} fieldData.width - Field width
 * @param {number} fieldData.height - Field height
 * @param {boolean} fieldData.is_required - Whether field is required
 * @param {boolean} fieldData.read_only - Whether field is read-only
 * @returns {Promise}
 */
export const createDocumentField = async (fieldData) => {
  try {
    console.log('📝 Creating document field:', fieldData);
    const response = await api.post('/document-field-create', fieldData);
    console.log('✅ Document field created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating document field:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Bulk create fields
 * API: POST /api/document-field-bulk-create
 * Recommended for creating multiple fields at once
 * @param {number} documentId - Document ID
 * @param {Array} fields - Array of field objects; each row is sent with document_id and page_number
 * @returns {Promise}
 */
export const bulkCreateFields = async (documentId, fields) => {
  try {
    console.log('📝 Bulk creating document fields:', { documentId, fieldsCount: fields.length });
    const fieldsWithDocumentId = (fields || []).map((field) => ({
      ...field,
      document_id: field?.document_id ?? documentId,
    }));
    const payload = {
      document_id: documentId,
      fields: fieldsWithDocumentId
    };
    
    const response = await api.post('/document-field-bulk-create', payload);
    console.log('✅ Document fields created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating document fields:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get all fields for a document
 * API: GET /api/document-fields/{document_id}
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const getDocumentFields = async (documentId) => {
  try {
    const response = await api.get(`/document-fields/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching document fields:', error);
    throw error;
  }
};

/**
 * Get a single field by ID
 * API: GET /api/document-field/{field_id}
 * @param {number} fieldId - Field ID
 * @returns {Promise}
 */
export const getDocumentField = async (fieldId) => {
  try {
    const response = await api.get(`/document-field/${fieldId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching document field:', error);
    throw error;
  }
};

/**
 * Update a field
 * API: PUT /api/document-field-update/{field_id}
 * Recommended for ongoing field management
 * @param {number} fieldId - Field ID
 * @param {Object} fieldData - Updated field data
 * @returns {Promise}
 */
export const updateDocumentField = async (fieldId, fieldData) => {
  try {
    console.log('🔄 Updating document field:', { fieldId, fieldData });
    const response = await api.request({
      method: 'PUT',
      url: `/document-field-update/${fieldId}`,
      data: fieldData,
    });
    console.log('✅ Document field updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating document field:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Bulk update existing fields (never creates rows)
 * API: PUT /api/document-field-bulk-update
 * @param {number} documentId - Document ID all field ids must belong to
 * @param {Array<Object>} fields - Each item must include numeric `id` (server field id) plus editable keys
 * @returns {Promise}
 */
export const bulkUpdateDocumentFields = async (documentId, fields) => {
  try {
    console.log('🔄 Bulk updating document fields:', { documentId, fieldsCount: fields?.length || 0 });
    const fieldsWithDocumentId = (fields || []).map((field) => ({
      ...field,
      document_id: field?.document_id ?? documentId,
    }));
    const payload = {
      document_id: documentId,
      fields: fieldsWithDocumentId,
    };
    const response = await api.put('/document-field-bulk-update', payload);
    console.log('✅ Document fields bulk-updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error bulk-updating document fields:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Delete a field
 * API: DELETE /api/document-field-delete/{field_id}
 * Recommended for ongoing field management
 * @param {number} fieldId - Field ID
 * @returns {Promise}
 */
export const bulkDeleteDocumentFields = async (fieldIds, documentId = null) => {
  try {
    const payload = { field_ids: fieldIds };
    if (documentId !== undefined && documentId !== null) {
      payload.document_id = documentId;
    }
    console.log('🗑️ Bulk deleting document fields:', {
      fieldCount: Array.isArray(fieldIds) ? fieldIds.length : 0,
      documentId,
    });
    const response = await api.post('/document-field-bulk-delete', payload);
    console.log('✅ Document fields bulk-deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error bulk-deleting document fields:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Delete a field
 * API: DELETE /api/document-field-delete/{field_id}
 * Recommended for ongoing field management
 * @param {number} fieldId - Field ID
 * @returns {Promise}
 */
export const deleteDocumentField = async (fieldId) => {
  try {
    console.log('🗑️ Deleting document field:', fieldId);
    const response = await api.delete(`/document-field-delete/${fieldId}`);
    console.log('✅ Document field deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting document field:', error);
    throw error;
  }
};

/**

* Delete envelope (draft only)
 * API: DELETE /api/envelope-delete/{id}
 * Only draft envelopes can be deleted. Sent, completed, or revoked envelopes cannot be deleted.
 * @param {number} envelopeId - Envelope ID
 * @returns {Promise}
 */
export const deleteEnvelope = async (envelopeId) => {
  try {
    console.log('🗑️ Deleting envelope:', envelopeId);
    const response = await api.delete(`/envelope-delete/${envelopeId}`);
    console.log('✅ Envelope deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting envelope:', error);
    throw error;
  }
};

/**
 * Create envelope recipient
 * API: POST /api/envelope-recipient-create
 * Recommended for ongoing recipient management
 * @param {number} envelopeId - Envelope ID
 * @param {Object} recipientData - Recipient data
 * @param {string} recipientData.email - Recipient email
 * @param {string} recipientData.name - Recipient name
 * @param {string} recipientData.type - 'signee' | 'review_recipient' | 'copy_recipient'
 * @param {number} recipientData.signature_order - Order for sequential workflow (optional)
 * @param {string} recipientData.signee_color - Color for signee (optional)
 * @returns {Promise}
 */
export const createEnvelopeRecipient = async (envelopeId, recipientData) => {
  try {
    console.log('👤 Creating envelope recipient:', { envelopeId, recipientData });
    const payload = {
      envelope_id: envelopeId,
      email: recipientData.email,
      name: recipientData.name,
      type: recipientData.type,
      signature_order: recipientData.signature_order,
      signee_color: recipientData.signee_color
    };
    
    const response = await api.post('/envelope-recipient-create', payload);
    console.log('✅ Envelope recipient created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating envelope recipient:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Update envelope recipient
 * API: PUT /api/envelope-recipient-update/{recipient_id}
 * Recommended for ongoing recipient management
 * @param {number} recipientId - Recipient ID
 * @param {Object} recipientData - Updated recipient data
 * @returns {Promise}
 */
export const updateEnvelopeRecipient = async (recipientId, recipientData) => {
  try {
    console.log('🔄 Updating envelope recipient:', { recipientId, recipientData });
    const response = await api.put(`/envelope-recipient-update/${recipientId}`, recipientData);
    console.log('✅ Envelope recipient updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating envelope recipient:', error);
    if (error.response?.data) {
      console.error('❌ Validation errors:', error.response.data);
    }
    throw error;
  }
};

/**
 * Delete envelope recipient
 * API: DELETE /api/envelope-recipient-delete/{recipient_id}
 * Recommended for ongoing recipient management
 * @param {number} recipientId - Recipient ID
 * @returns {Promise}
 */
export const deleteEnvelopeRecipient = async (recipientId) => {
  try {
    console.log('🗑️ Deleting envelope recipient:', recipientId);
    const response = await api.delete(`/envelope-recipient-delete/${recipientId}`);
    console.log('✅ Envelope recipient deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting envelope recipient:', error);
    throw error;
  }
};



